import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { CultureCode, useEventLocaleCulture, useEventLocaleCurrency } from '@shared/hooks/useGeoLocale';
import { useResolvedCashFundLocale } from '@shared/utils/currency/useResolvedCashFundLocale';
import { useIsInternationalRegistryRoute } from './useIsInternationalRegistryRoute';

const UK_COUNTRY_CODE = 'GBR';

export type InternationalRegistryExperience = Readonly<{
  isInternationalRegistry: boolean;
  loading: boolean;
}>;

export type InternationalExternalRegistries = Readonly<{
  showUkRetailers: boolean;
  linkOnlyExternalRegistries: boolean;
  loading: boolean;
}>;

/**
 * Shared base: resolves the raw signals both public hooks need — the lean flag,
 * the event locale culture, and the currency-derived supported (country, currency)
 * pair — exactly once, so consumers don't re-run the same underlying hooks.
 */
const useInternationalRegistryBase = () => {
  const [isLeanInternationalRegistryEnabled, isLeanFlagLoading] = useIsFeatureEnabled('leanInternationalRegistryEnabled');
  const eventLocaleCulture = useEventLocaleCulture();
  const eventLocaleCurrency = useEventLocaleCurrency();
  const { supported, loading: localeLoading } = useResolvedCashFundLocale(eventLocaleCurrency);

  const isInternationalEvent = eventLocaleCulture !== undefined && eventLocaleCulture !== CultureCode.US;
  const isCandidate = isLeanInternationalRegistryEnabled && isInternationalEvent;

  return {
    eventLocaleCulture,
    supported,
    localeLoading,
    isInternationalRegistry: isCandidate && supported !== null,
    loading: isInternationalEvent && (isLeanFlagLoading || (isCandidate && localeLoading)),
  };
};

/**
 * Decides whether the event gets the lean international registry experience
 * (cash-fund-focused, no US catalog). True when the flag is on, the event's
 * creation locale is international, and the event-locale-derived currency is a
 * BE-supported cash fund currency.
 */
export const useInternationalRegistryExperience = (): InternationalRegistryExperience => {
  const { isInternationalRegistry, loading } = useInternationalRegistryBase();
  return { isInternationalRegistry, loading };
};

/**
 * External registry offering for the lean international experience, scoped to the
 * ACTIVE route (`/registry/intl`), not event eligibility. On the lean route every
 * connection is restricted to connect-by-URL (the transfer dialog forces the link
 * action; sync/transfer never surface), and UK events additionally get the UK
 * retailer tiles (uk-cambium BE section) as link entry points. On the US route
 * every flag is false, so the US experience — including the Amplitude-geo-targeted
 * Cambium UK offering, which keys off its own feature flag — is unaffected even for
 * an international-eligible account. `loading` reflects the UK-retailer resolution
 * (the currency→country lookup) and is only relevant on the lean route, where the
 * transfer funnel must wait for it before capturing its start screen.
 */
export const useInternationalExternalRegistries = (): InternationalExternalRegistries => {
  const { eventLocaleCulture, supported, localeLoading } = useInternationalRegistryBase();
  const isInternationalRoute = useIsInternationalRegistryRoute();

  // Key UK off the resolved payout country (GBP resolves only to GBR, and it is
  // available pre-bank-connection), not the event-creation culture — a registry
  // can settle in GBP while its culture code is not literally GB. Culture is kept
  // as a fallback signal.
  const isUkEvent = supported?.countryCode === UK_COUNTRY_CODE || eventLocaleCulture === CultureCode.GB;

  return {
    showUkRetailers: isInternationalRoute && isUkEvent,
    linkOnlyExternalRegistries: isInternationalRoute,
    loading: isInternationalRoute && localeLoading,
  };
};
